import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Row, Col, Button, Card, Form, Modal, Avatar, notification, Tabs, Radio, Input, Alert } from 'antd';

import { withAppContext } from 'context/AppContext';
import { getRoomTypeAmenitiesConstant, getBedTypesConstant } from 'utils/apis/constants';
import { putUpdateRoomType, deleteRemoveRoomTypeValidation, deleteRoomType } from 'utils/apis/roomtype';
import { putUpdateRate } from 'utils/apis/rate';
import { capitalizeFirstLetter, convertAmenitiesSelectionToObj, generateDisplayFee, generateFeeFromDisplay, guard } from 'utils/general';
import { buildIOTAFormUri, buildListingUri, buildServicePackageEditUri } from 'utils/routes';

import airbnbLogo from 'images/airbnb-logo.png';
import ctripLogo from 'images/ctrip-logo.png';
import bookingcomLogo from 'images/booking-logo-icon.png';

import BasicDetailsCard from './components/BasicDetailsCard/BasicDetailsCard';
import BookingEngineConfigDetailsCard from './components/BookingEngineConfigDetailsCard/BookingEngineConfigDetailsCard';
import SetupCard from './components/SetupCard/SetupCard';
import PhotosCard from '../components/PhotosCard/PhotosCard';
import AmenitiesCard from '../components/AmenitiesCard/AmenitiesCard';
import FinePrintInfoCard from '../Unit/components/FinePrintInfoCard/FinePrintInfoCard';
import FormInputNumber from 'components/FormInputNumber/FormInputNumber';
import intl from 'react-intl-universal';

import styles from './RoomType.module.css';
import { listingDeletionReasons } from 'utils/constants';

const TabPane = Tabs.TabPane;
class RoomType extends React.Component {
  constructor() {
    super();

    this.state = {
      hasFetchedBedTypes: false,
      bedTypesSelection: {},
      hasFetchedRoomTypeAmenities: false,
      roomTypeAmenities: {},
      isSaving: false,
      isDeleting: false,
      deletionReasonModal: false,
      selectedReason: null,
      otherReason: ''
    };
  }

  componentDidMount() {
    this.fetchBedTypes();
    this.fetchRoomTypeAmenities();
  }

  fetchBedTypes = () => {
    getBedTypesConstant()
      .then(res => {
        this.setBedTypesState(res);
      })
      .catch(err => console.error(err));
  };

  fetchRoomTypeAmenities = () => {
    getRoomTypeAmenitiesConstant()
      .then(res => {
        this.setRoomTypeAmenitiesState(res);
      })
      .catch(err => console.error(err));
  };

  setBedTypesState = bedTypes => {
    this.setState({
      hasFetchedBedTypes: true,
      bedTypesSelection: bedTypes
    });
  };

  setRoomTypeAmenitiesState = roomTypeAmenities => {
    let formattedRoomTypeAmenities = {};
    Object.keys(roomTypeAmenities).forEach(key => {
      const { code, label, type, airbnb, ctrip, bookingcom } = roomTypeAmenities[key];
      let formattedLabel = (
        <div>
          {intl.get(`listings.roomType.roomAmenities.${label}`).d(label)}{' '}
          {airbnb && (
            <>
              <Avatar size={15} src={airbnbLogo} style={{ marginBottom: 3 }} />{' '}
            </>
          )}
          {bookingcom && (
            <>
              <Avatar size={15} src={bookingcomLogo} style={{ marginBottom: 3 }} />{' '}
            </>
          )}
          {ctrip && (
            <>
              <Avatar size={15} src={ctripLogo} style={{ marginBottom: 3 }} />{' '}
            </>
          )}
        </div>
      );
      if (type in formattedRoomTypeAmenities) {
        formattedRoomTypeAmenities[type]['data'].push({
          key: code,
          value: formattedLabel
        });
      } else {
        formattedRoomTypeAmenities[type] = {
          icon: this.getRoomTypeAmenitiesIcon(type),
          data: [
            {
              key: code,
              value: formattedLabel
            }
          ]
        };
      }
    });
    this.setState({
      hasFetchedRoomTypeAmenities: true,
      roomTypeAmenities: formattedRoomTypeAmenities
    });
  };

  getRoomTypeAmenitiesIcon = type => {
    const icons = {
      Accessibility: 'bell',
      Bathroom: 'skin',
      'Food and Drink': 'coffee',
      'Media Technology': 'home',
      'Outdoor and View': 'cloud-o',
      'Room Amenities': 'hdd',
      'Service and Extras': 'smile-o'
    };
    return icons[type] || 'tool';
  };

  extractBasicDetailsData = () => {
    const { data } = this.props;
    return {
      name: data.name,
      roomSizeInSqFt: data.roomSizeInSqFt,
      weekdayPrice: data.rate.weekday,
      weekendPrice: data.rate.weekend,
      adultCapacity: data.capacity.adult,
      childrenCapacity: data.capacity.children
    };
  };

  extractFinePrintInfoData = () => {
    const { data } = this.props;
    return { finePrint: data.finePrint ? data.finePrint : '' };
  };

  extractSetupData = () => {
    const { data } = this.props;
    const { bedTypesSelection } = this.state;

    return {
      bathrooms: data.bathrooms,
      livingrooms: data.livingrooms,
      bedrooms: data.bedrooms
        ? data.bedrooms.map(bedroom => {
            let totalBedCount = 0;
            const beds = Object.keys(bedTypesSelection).reduce((newBeds, key) => {
              let newBed = {
                ...bedTypesSelection[key],
                count: 0
              };
              for (let i = 0; i < bedroom.beds.length; i++) {
                const bed = bedroom.beds[i];
                if (String(bed.type) === String(bedTypesSelection[key].code)) {
                  newBed.count = bed.count;
                  totalBedCount += bed.count;
                  break;
                }
              }
              return {
                ...newBeds,
                [key]: newBed
              };
            }, {});

            return {
              ...bedroom,
              beds,
              totalBedCount
            };
          })
        : undefined
    };
  };

  extractAmenitiesData = () => {
    const { data } = this.props;
    const { roomTypeAmenities } = this.state;
    return convertAmenitiesSelectionToObj(data.amenities, roomTypeAmenities);
  };

  extractPhotosData = () => {
    const { data } = this.props;
    return {
      photos:
        data.images && data.images.length > 0
          ? data.images.map(image => ({
              link: image.imageUrl,
              caption: image.caption[0] && image.caption[0].text ? image.caption[0].text : ''
            }))
          : []
    };
  };

  extractBookingEngineConfigDetailsData = () => {
    const { data } = this.props;
    return {
      bookingEngineRoomTypeDisplayName: guard(() => data.bookingEngine.externalDisplayName),
      bookingEngineRoomTypeCleaningFee: guard(() => generateDisplayFee(data.bookingEngine.priceDetail.cleaningFee, false))
    };
  };

  formatAmenities = formFields => {
    const newFormFields = { ...formFields };
    const { roomTypeAmenities } = this.state;
    const filtteredAmenitiesFormData = Object.keys(roomTypeAmenities).reduce((formattedData, key) => {
      const newKey = String(key).toLowerCase();
      const newData = {
        ...formattedData,
        [newKey]: formFields[newKey]
      };
      // Remove key from form
      delete newFormFields[newKey];
      return newData;
    }, {});

    const formattedAmenities = Object.keys(filtteredAmenitiesFormData).reduce((newData, key) => {
      if (filtteredAmenitiesFormData[key] && Array.isArray(filtteredAmenitiesFormData[key])) {
        return [...newData, ...filtteredAmenitiesFormData[key]];
      }
      return newData;
    }, []);

    return {
      ...newFormFields,
      amenities: formattedAmenities
    };
  };

  formatImages = formFields => {
    return {
      ...formFields,
      photos: undefined,
      images:
        formFields.photos && formFields.photos.length > 0
          ? formFields.photos.map(data => ({
              imageUrl: data.link,
              caption: data.caption ? [{ text: data.caption }] : undefined
            }))
          : []
    };
  };

  formatCapacity = formFields => {
    return {
      ...formFields,
      capacity: {
        adult: formFields.adultCapacity,
        children: formFields.childrenCapacity
      },
      adult: undefined,
      children: undefined
    };
  };

  formatBedrooms = formFields => {
    return {
      ...formFields,
      bedrooms: formFields.bedrooms.map(bedroom => {
        const { beds } = bedroom;
        const newBeds = Object.keys(beds)
          .map(bedKey => {
            const bed = beds[bedKey];
            return bed.count
              ? {
                  type: bed.code,
                  count: bed.count
                }
              : undefined;
          })
          .filter(bed => !!bed); // filter bed that has count 0
        return { name: bedroom.name, beds: newBeds };
      })
    };
  };

  formatBookingEngineConfigDetails = formFields => {
    return {
      ...formFields,
      bookingEngine: {
        externalDisplayName: formFields.bookingEngineRoomTypeDisplayName,
        priceDetail: {
          cleaningFee: generateFeeFromDisplay(formFields.bookingEngineRoomTypeCleaningFee)
        }
      }
    };
  };

  handleModalOk = async () => {
    const { history, data } = this.props;
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const { selectedReason, otherReason } = values;

        // Determine the reason to use
        const reason = selectedReason === 'others' ? otherReason : selectedReason;
        console.log(reason);

        try {
          // Call the updateDeletedUnit function
          await deleteRoomType(data._id, reason);

          // Close the modal after action
          this.setState({
            deletionReasonModal: false,
            selectedReason: null // Reset selection after modal closes
          });

          // Show success notification
          notification.success({
            message: intl.get('listings.roomType.message.roomTypeDeleted').d('Your Room Type has been deleted!')
          });

          // Redirect to the listing URI
          history.push(buildListingUri());
        } catch (error) {
          // Handle any errors that occur during the update
          console.error('Error updating room type:', error);
          notification.error({
            message: intl.get('listings.unit.message.errorOccured').d('An Error Occured'),
            description: intl.get('listings.roomType.message.unableDelete').d('Unable to delete this Room Type, Please try again later')
          });
        }
      }
    });
  };

  // Handle radio button change
  handleOptionChange = e => {
    this.setState({
      selectedReason: e.target.value
    });
  };

  handleOptionChange = e => {
    this.setState({
      selectedReason: e.target.value,
      otherReason: '' // reset 'otherReason' if another option is selected
    });
  };

  removePrice = formFields => {
    return {
      ...formFields,
      weekdayPrice: undefined,
      weekendPrice: undefined
    };
  };

  generateRatePayload = rate => formFields => {
    return {
      ...rate,
      // Removing unnessary mongo fields from passing to API
      createdAt: undefined,
      createdBy: undefined,
      _v: undefined,
      _id: undefined,
      weekday: formFields.weekdayPrice,
      weekend: formFields.weekendPrice
    };
  };

  handleOnSave = () => {
    const { form, data, allowBookingEngine } = this.props;
    const roomTypePayload =
      allowBookingEngine === true
        ? [form.getFieldsValue()]
            .map(this.formatAmenities)
            .map(this.formatImages)
            .map(this.formatCapacity)
            .map(this.formatBedrooms)
            .map(this.formatBookingEngineConfigDetails)
            .map(this.removePrice)[0]
        : [form.getFieldsValue()]
            .map(this.formatAmenities)
            .map(this.formatImages)
            .map(this.formatCapacity)
            .map(this.formatBedrooms)
            .map(this.removePrice)[0];
    const ratePayload = [form.getFieldsValue()].map(this.generateRatePayload(data.rate))[0];

    this.setState({
      isSaving: true
    });

    const updateRoomTypePromise = putUpdateRoomType(data._id, roomTypePayload);
    const updateRatePromise = putUpdateRate(data.rate._id, ratePayload);
    return Promise.all([updateRoomTypePromise, updateRatePromise])
      .then(() => {
        this.setState({
          isSaving: false
        });
        notification.success({
          message: intl.get('listings.roomType.message.updated').d('Your room type has been updated!')
        });
      })
      .catch(e => {
        this.setState({
          isSaving: false
        });
        notification.error({
          message: intl.get('listings.roomType.message.updateError').d('Something went wrong and your room type is not updated')
          // description: e.message
        });
      });
  };

  handleOnFormSubmit = e => {
    e.preventDefault();
    const { form } = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        const onOkAction = this.handleOnSave;
        Modal.confirm({
          title: intl.get('listings.roomType.message.updateTitle').d('Do you want to update this room type?'),
          content: intl
            .get('listings.roomType.message.updateContent')
            .d('Updating this room type will affect all units that tied to this room type.'),
          onOk() {
            return onOkAction();
          },
          onCancel() {}
        });
      }
    });
  };

  onDeleteRoomType = () => {
    const { data, history } = this.props;
    this.setState({
      isDeleting: true
    });
    deleteRemoveRoomTypeValidation(data._id)
      .then(() => {
        this.setState({
          isDeleting: false,
          deletionReasonModal: true // show modal after deletion
        });
      })
      .catch(err => {
        this.setState({
          isDeleting: false
        });
        if (err.response && err.response.status && err.response.status === 400) {
          const errorRes = err.response.data || {};
          this.roomTypeDeleteErrorMessage(errorRes);
        } else {
          //message.error('Oops! Something went wrong. Please try again later.');
        }
      });
  };

  roomTypeDeleteErrorMessage = errorRes => {
    const { unitData } = this.props;
    let isUnitInRelationshipMsg = [];
    let servicePackageNameMsg = [];
    let syncedOtasErrMsgMsg = [];
    let actionIndex = 0;
    let roomTypeOtasMsgDisplayed = false;

    console.log(errorRes);

    Modal.error({
      title: intl.get('listings.roomType.message.unableDelete').d('Unable to delete this Room Type'),
      content: (
        <div>
          {errorRes.map((error, index) => {
            const isUnitInRelationship = error.isUnitInRelationship;
            const servicePackageName = error.servicePackage && error.servicePackage.name;
            const servicePackageID = error.servicePackage && error.servicePackage._id;
            const syncedOtas = error.syncedOtas;
            const roomTypeOTAs = error.roomTypeOTAs;

            if (isUnitInRelationship) {
              isUnitInRelationshipMsg.push(isUnitInRelationship);
            }
            if (servicePackageName) {
              servicePackageNameMsg.push(servicePackageName);
            }
            if (syncedOtas && syncedOtas.length > 0) {
              syncedOtasErrMsgMsg.push(syncedOtas);
            }
            if (roomTypeOTAs.length > 0) {
              syncedOtasErrMsgMsg.push(roomTypeOTAs);
            }

            const roomTypeOtasMsg = roomTypeOTAs.length !== 0 && !roomTypeOtasMsgDisplayed && (
              <p>
                {intl.get('listings.roomType.message.syncedWith').d('This room type is synced with')}{' '}
                <b>
                  <a
                    href={buildIOTAFormUri({ propertyId: unitData.roomType.property._id, unitId: error.unitId, integrationType: 'hotel' })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {roomTypeOTAs
                      .map(syncedOta => capitalizeFirstLetter(syncedOta))
                      .reduce(
                        (roomTypeOtasErrMsg, source, i) =>
                          `${roomTypeOtasErrMsg}${
                            i === roomTypeOTAs.length - 1 ? `${intl.get('listings.roomType.message.and').d('and')}` : ', '
                          }${source}`
                      )}
                  </a>
                </b>{' '}
                <hr />
              </p>
            );
            //Ensure that the display message only Display ONCE !
            if (roomTypeOtasMsg) {
              roomTypeOtasMsgDisplayed = true;
            }

            const servicePackageErrMsg = servicePackageName && (
              <span>
                {intl.get('listings.unit.message.packagedUnder').d('packaged under')}{' '}
                <a href={buildServicePackageEditUri(servicePackageID)} target="_blank" rel="noopener noreferrer">
                  <b>{servicePackageName}</b>
                </a>{' '}
                {intl.get('listings.unit.message.servicePackage').d('service package')}
              </span>
            );

            const syncedOtasErrMsg = !!syncedOtas && Array.isArray(syncedOtas) && syncedOtas.length > 0 && (
              <span>
                {intl.get('listings.unit.message.syncWith').d('synced with')}{' '}
                <b>
                  <a
                    href={buildIOTAFormUri({ propertyId: unitData.roomType.property._id, unitId: error.unitId })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {syncedOtas
                      .map(syncedOta => capitalizeFirstLetter(syncedOta))
                      .reduce(
                        (syncedOtasErrMsg, source, i) =>
                          `${syncedOtasErrMsg}${
                            i === syncedOtas.length - 1 ? `${intl.get('listings.roomType.message.and').d(' and ')}` : ', '
                          }${source}`
                      )}
                  </a>
                </b>
              </span>
            );

            return (
              <div key={index}>
                {roomTypeOtasMsg}
                {error.unitName && (
                  <div>
                    <p>
                      {error.unitName} {intl.get('listings.roomType.message.is').d('is')}
                      {isUnitInRelationship && `${intl.get('listings.unit.message.havingParentChild').d('having parent-child relation')}`}
                      {isUnitInRelationship && servicePackageName && `${intl.get('listings.roomType.message.and').d(' and ')}`}
                      {servicePackageErrMsg}
                      {(isUnitInRelationship || servicePackageName) && syncedOtasErrMsg && `${intl.get('listings.roomType.message.and').d(' and ')}`}
                      {syncedOtasErrMsg}.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
          <hr />
          <p>
            {intl.getHTML('listings.roomType.message.toDelete').d('To delete this Room Type, please')}:
            {isUnitInRelationshipMsg && isUnitInRelationshipMsg.length > 0 ? (
              <div>
                {++actionIndex}) {intl.getHTML('listings.unit.message.unlink').d('Unlink the <b>parent-child unit(s)</b>')}
              </div>
            ) : null}
            {servicePackageNameMsg && servicePackageNameMsg.length > 0 ? (
              <div>
                {++actionIndex}) {intl.getHTML('listings.roomType.message.removeFromServicePackage').d('Remove all units from the service package')}
              </div>
            ) : null}
            {syncedOtasErrMsgMsg && syncedOtasErrMsgMsg.length > 0 ? (
              <div>
                {++actionIndex}) {intl.get('listings.roomType.message.desyncAll').d('Desync all listings OTAs')}
              </div>
            ) : null}
          </p>
          <p></p>
        </div>
      )
    });
  };

  handleOnDelete = e => {
    e.preventDefault();

    const onOkAction = this.onDeleteRoomType;

    Modal.confirm({
      title: intl.get('listings.roomType.message.deleteTitle').d('Are you sure you want to delete this room type?'),
      content: (
        <>
          {intl
            .get('listings.roomType.message.deleteContent')
            .d('Deleting this room type will also delete ALL the unit(s) that are linked to this room type')}
          <Alert
            message={intl
              .get('listings.roomType.message.deleteAlert')
              .d('All data will be removed upon room type deletion. Please download necessary data (reservations, reports, payouts) beforehand.')}
            type="warning"
            showIcon
            style={{ marginTop: '8px' }}
          />
        </>
      ),
      onOk() {
        return onOkAction();
      },
      onCancel() {},
      okButtonProps: { id: 'del-cfm-button3a-editlisting' },
      cancelButtonProps: { id: 'canceldel-cfm-button3b-editlisting' }
    });
  };

  render() {
    const { form, hasFetchedUnit, checkIsAllowDeleteListing, allowBookingEngine, checkIsAdminReadOnly } = this.props;
    const { bedTypesSelection, hasFetchedRoomTypeAmenities, roomTypeAmenities, isSaving, isDeleting } = this.state;
    return (
      <div>
        <Form onSubmit={this.handleOnFormSubmit} style={{ width: '100%' }}>
          <Tabs type="card" className={styles.unitTab} defaultActiveKey="1">
            <TabPane
              tab={intl.get('listings.unit.headerLabels.general').d('General Details')}
              key="1"
              className={styles.unitTabPane}
              forceRender={hasFetchedUnit}
            >
              <BasicDetailsCard
                form={form}
                cardClassname={styles.cardContainer}
                defaultValues={this.extractBasicDetailsData()}
                currency={this.props.data.currency}
                timezone={this.props.data.timezone}
              />
              <SetupCard form={form} cardClassname={styles.cardContainer} defaultValues={this.extractSetupData()} bedTypes={bedTypesSelection} />
              <AmenitiesCard
                form={form}
                cardClassname={styles.cardContainer}
                hasFetchedAmenities={hasFetchedRoomTypeAmenities}
                amenities={roomTypeAmenities}
                defaultValues={this.extractAmenitiesData()}
              />
              <PhotosCard
                form={form}
                cardClassname={styles.cardContainer}
                captionPrefix="Room Type Image"
                defaultValues={this.extractPhotosData()}
                hasFetchedPhotos={hasFetchedUnit}
              />
              <Card title="Security Deposit Override" className={styles.cardContainer}>
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInputNumber
                      form={form}
                      name="securityDepositAmount"
                      label="Security Deposit Amount (leave empty to use property default)"
                      defaultValue={this.props.data.securityDepositAmount}
                      minValue={0}
                      precision={2}
                      size="large"
                      placeholder="Use property default"
                    />
                  </Col>
                </Row>
              </Card>
            </TabPane>
            {allowBookingEngine === true && (
              <TabPane
                tab={intl.get('host.headerLabels.bookingEngineConfiguration').d('Booking Website Configuration')}
                key="2"
                className={styles.unitTabPane}
                forceRender={hasFetchedUnit}
              >
                <BookingEngineConfigDetailsCard
                  form={form}
                  cardClassname={styles.cardContainer}
                  defaultValues={this.extractBookingEngineConfigDetailsData()}
                />
                <FinePrintInfoCard form={form} cardClassname={styles.cardContainer} defaultValues={this.extractFinePrintInfoData()} />
              </TabPane>
            )}
          </Tabs>
          <Row type="flex" justify="start" gutter={8}>
            <Col>
              <Button
                id="save-button3-editlisting"
                type="primary"
                className={styles.marginRight}
                size="large"
                htmlType="submit"
                loading={isSaving}
                disabled={checkIsAdminReadOnly()}
              >
                {isSaving ? intl.get('listings.unit.headerLabels.saving').d('Saving') : intl.get('listings.unit.headerLabels.save').d('Save')}
              </Button>
            </Col>
            <Col>
              {checkIsAllowDeleteListing() && (
                <Button
                  id="del-button3-editlisting"
                  type="danger"
                  size="large"
                  onClick={this.handleOnDelete}
                  loading={isDeleting}
                  disabled={checkIsAdminReadOnly()}
                >
                  {intl.get('listings.unit.headerLabels.delete').d('Delete')}
                </Button>
              )}
            </Col>
          </Row>
        </Form>
        {this.state.deletionReasonModal && (
          <Modal
            title={intl.get('listings.roomType.headerLabels.roomTypeDeleted').d('Room Type Deleted')}
            visible={this.state.deletionReasonModal}
            onOk={this.handleModalOk}
            okText={intl.get('listings.unit.headerLabels.confirm').d('Confirm')}
            cancelButtonProps={{ style: { display: 'none' } }}
            closable={false}
          >
            <p>
              {intl
                .get('listings.roomType.message.deleteReason')
                .d('The room type has been deleted successfully. May we ask what is the reason leading to the deletion of this listing?')}
            </p>
            <Form>
              <Form.Item>
                {form.getFieldDecorator('selectedReason', {
                  rules: [{ required: true, message: intl.get('listings.unit.placeholder.selectReason').d('Please select a reason for deletion') }]
                })(
                  <Radio.Group onChange={this.handleOptionChange} value={this.state.selectedReason}>
                    {listingDeletionReasons.map(reason => (
                      <div key={reason.value}>
                        <Radio value={reason.key}>{intl.get(`listings.listingDeletionReason.${reason.label}`).d(reason.label)}</Radio>
                      </div>
                    ))}
                    <div>
                      <Radio value="others">{intl.get('listings.unit.headerLabels.others').d('Others')}</Radio>
                    </div>
                  </Radio.Group>
                )}
              </Form.Item>

              {this.state.selectedReason === 'others' && (
                <Form.Item>
                  {form.getFieldDecorator('otherReason', {
                    rules: [
                      {
                        required: true,
                        message: intl.get('listings.unit.placeholder.provideReason').d(`Please provide a reason if selecting 'Others'`)
                      }
                    ]
                  })(
                    <Input.TextArea
                      placeholder={intl.get('listings.unit.placeholder.specifyReason').d(`Please specify your reason`)}
                      rows={4}
                      style={{ marginTop: 8 }}
                    />
                  )}
                </Form.Item>
              )}
            </Form>
          </Modal>
        )}
      </div>
    );
  }
}

RoomType.propTypes = {
  form: PropTypes.object.isRequired,
  checkIsAdmin: PropTypes.func,
  data: PropTypes.object,
  hasFetchedUnit: PropTypes.bool,

  allowBookingEngine: PropTypes.bool
};

RoomType.defaultProps = {
  checkIsAdmin: () => {},
  data: {},
  hasFetchedUnit: false,
  allowBookingEngine: false
};

const WrappedFormRoomType = Form.create()(RoomType);

export default withRouter(withAppContext(WrappedFormRoomType));
